import { ReactElement, ReactNode, useMemo } from 'react';
import { ApexOptions } from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import { AuxPlotSeries, TraverseSegment } from './auxSensorChannels';
import { auxBaseOptions } from './auxPlotStyle';
import AuxPlotCard from './AuxPlotCard';

interface AuxSensorPlotProps {
  title: string;
  unit: string;
  series: AuxPlotSeries[];
  isLoading?: boolean;
  /** When true, render a disabled placeholder instead of a chart. */
  inactive?: boolean;
  /** Optional control rendered to the right of the title (e.g. a mode toggle). */
  headerExtra?: ReactNode;
  /** Optional full-width control row below the header (e.g. the time-offset slider). */
  controls?: ReactNode;
  /** Shaded start/stop bands for each traverse (shown in Traverse mode). */
  traverseSegments?: TraverseSegment[];
}

export type { AuxPlotSeries };

// Distinct, high-contrast colours so two same-type sensors are easy to tell
// apart. Matches the app's existing ApexCharts palette ordering.
const COLOR_PALETTE = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#10B981', // Green
  '#F59E0B', // Amber
  '#8B5CF6', // Purple
  '#06B6D4', // Cyan
];

const CHART_HEIGHT = 360;

/**
 * A single line chart overlaying N same-type sensor instances on a shared
 * relative-time x-axis. Used three times by the Aux Sensor tab (power, wheel
 * speed, dynamic pressure). The curve is drawn `straight` (not smoothed) so the
 * raw samples are shown honestly for cross-sensor comparison.
 */
export default function AuxSensorPlot({
  title,
  unit,
  series,
  isLoading = false,
  inactive = false,
  headerExtra,
  controls,
  traverseSegments,
}: AuxSensorPlotProps): ReactElement {
  const plotSeries = useMemo(
    () => series
      .filter((s) => s.points.some((p) => p[1] != null))
      .map((s) => ({ name: s.label, data: s.points })),
    [series],
  );

  const options = useMemo<ApexOptions>(() => {
    const base = auxBaseOptions(CHART_HEIGHT);
    return {
      ...base,
      annotations: {
        xaxis: (traverseSegments ?? []).map((seg) => ({
          x: seg.startS,
          x2: seg.endS,
          fillColor: '#3B82F6',
          opacity: 0.07,
          label: {
            text: `T${seg.lap}`,
            position: 'top' as const,
            style: { color: '#1E3A8A', background: '#EFF6FF', fontSize: '10px' },
          },
        })),
      },
      chart: { ...base.chart, type: 'line', zoom: { enabled: true, type: 'xy', autoScaleYaxis: false } },
      colors: COLOR_PALETTE,
      stroke: { curve: 'straight', width: 1.5 },
      markers: { size: 0 },
      xaxis: {
        type: 'numeric',
        title: { text: 'Time (seconds)', style: { fontSize: '13px', fontWeight: 600 } },
        labels: { formatter: (value: string) => `${parseFloat(value).toFixed(0)}s` },
        decimalsInFloat: 0,
      },
      yaxis: {
        title: { text: `${title} (${unit})`, style: { fontSize: '13px', fontWeight: 600 } },
        labels: { formatter: (value: number) => (value != null ? value.toFixed(1) : '') },
      },
      tooltip: {
        shared: true,
        x: { formatter: (value: number) => `${value.toFixed(2)} s` },
        y: { formatter: (value: number) => (value != null ? `${value.toFixed(2)} ${unit}` : '') },
      },
    };
  }, [title, unit, traverseSegments]);

  if (inactive) {
    return (
      <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md cr-opacity-70">
        <div className="cr-mb-2 cr-flex cr-items-center cr-gap-2">
          <h2 className="cr-text-lg cr-font-semibold cr-text-gray-400">{title}</h2>
          <span
            className="cr-rounded cr-bg-gray-100 cr-px-2 cr-py-0.5 cr-text-xs
              cr-font-medium cr-text-gray-400"
          >
            Coming soon
          </span>
        </div>
        <div className="cr-flex cr-h-32 cr-items-center cr-justify-center cr-text-sm cr-text-gray-400">
          {title} comparison is not available yet.
        </div>
      </div>
    );
  }

  return (
    <AuxPlotCard
      title={title}
      headerExtra={headerExtra}
      controls={controls}
      isLoading={isLoading}
      loadingLabel={`Loading ${title.toLowerCase()}…`}
      isEmpty={plotSeries.length === 0}
      emptyMessage={`No ${title.toLowerCase()} data available for this run.`}
    >
      <ReactApexChart options={options} series={plotSeries} type="line" height={CHART_HEIGHT} />
    </AuxPlotCard>
  );
}
